import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ContextCardProps } from '../ContextCard';
import {
  LIFE_EVENT_ENROLLMENT_TYPE,
  NEW_HIRE_ENROLLMENT_TYPE,
  OPEN_ENROLLMENT_TYPE,
} from 'theSource/queries';
import { AlertBox, Card } from 'components';
import {
  ENROLLMENT_STATUSES, formatDateString,
  INCOMPLETE_ENROLLMENT_STATUSES,
} from '../../utils';
import { PsButtonTuner } from 'stencilSetup';
import {
  getLastViewedPage,
  getPageRouter,
} from 'enrollment/utils/common/Utils';
import { setEnrollmentLatest } from 'enrollment/components/BottomNavigation/services';
import { calcDaysLeft } from 'enrollment/features/EnrollmentDashboard/utils/utils';
import { PSBannerWrapper } from 'enrollment/components/Shared';
import { CheckCircleStyled } from 'enrollment/components/Header/components/CartDrawer/CartDrawerStyled';
import { DownloadConfirmation } from 'components/DownloadConfirmation';
import { EmailConfirmationModal } from 'components/EmailConfirmationModal';
import { ROUTES } from 'enrollment/app/routes';
import { ROUTES as SOURCE_ROUTES } from 'theSource/app/routes';
import { setEnrollmentContext } from 'theSource/features/Home/components/EnrollmentBanner/services';
import { selectSubscriberDetails } from 'enrollment/features/Profile/services';
import { selectLumosEnabled, selectOldEnrollmentUrl } from 'theSource/features';

export const EnrollmentCard = ({
  enrollmentContext,
  cardIndex,
  allContexts,
  planYearStart,
  planYearEnd,
}: ContextCardProps) => {
  const { t } = useTranslation('pages.home');
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const subscriberData = useSelector(selectSubscriberDetails);
  const profileAvatar = subscriberData?.subscriber_profile?.avatar;
  const profilePicture = subscriberData?.subscriber_profile?.profile_picture;
  const lumosEnabled = useSelector(selectLumosEnabled);
  const oldEnrollmentUrl = useSelector(selectOldEnrollmentUrl);

  const context = enrollmentContext.enrollment_type_lookup_code;
  const status = enrollmentContext.status;
  const continueEnrollment = enrollmentContext.status === 'started';
  const notstartedEnrollment = enrollmentContext.status === 'not_started';
  const statusIndex = ENROLLMENT_STATUSES.indexOf(enrollmentContext.status);

  const isCompleteAndDual =
    status === 'complete' && allContexts && allContexts.length > 1;
  const isCompleteNotDual = status === 'complete' && allContexts?.length === 1;

  const isNHandDual =
    context === NEW_HIRE_ENROLLMENT_TYPE &&
    allContexts &&
    allContexts.length > 1 &&
    status !== 'complete';
  const headerText = new Map<string, string>([
    [NEW_HIRE_ENROLLMENT_TYPE, t('newHireEnrollmentHeader')],
    [OPEN_ENROLLMENT_TYPE, t('openEnrollmentHeader')],
  ]);

  const lastViewedPage = getLastViewedPage({
    currentCategory: enrollmentContext.current_category,
    requiredTask: enrollmentContext.required_tasks[0] || 'confirm',
  });

  const numPlanYears = enrollmentContext.included_plan_years;

  let enrollmentPlanYearEndsOn = planYearEnd?.toString();

  if (numPlanYears && numPlanYears.length > 1) {
    // calculate end date when overalapping plan years
    const endsOnDates = numPlanYears.map(
      (py) => py.ends_on
    );
    enrollmentPlanYearEndsOn = formatDateString(endsOnDates
      .reduce((date0, date1) => (date0 > date1 ? date0 : date1))
      .toString());
  };

  const bodyText = new Map<string, string[]>([
    [
      NEW_HIRE_ENROLLMENT_TYPE,
      [
        t('newHireEnrollmentBody'),
        t('newHireEnrollmentBody'),
        t('continueEnrollment'),
        t('completedEnrollment'),
      ],
    ],
    [
      OPEN_ENROLLMENT_TYPE,
      [
        t('openEnrollmentBody'),
        t('openEnrollmentBody'),
        t('continueEnrollment'),
        t('completedEnrollment'),
      ],
    ],
  ]);

  const titleText = new Map<string, string>([
    [NEW_HIRE_ENROLLMENT_TYPE, t('lePlanNext')],
    [OPEN_ENROLLMENT_TYPE, t('planNext')],
  ]);

  const navToEnrollment = () => {
    if (lumosEnabled) {
      dispatch(setEnrollmentContext(context));
      dispatch(setEnrollmentLatest(lastViewedPage));
      if (notstartedEnrollment) {
        navigate(
          `${SOURCE_ROUTES.enrollment.path}/${ROUTES.enrollment.beforeStarted.path}`
        );
      } else if (continueEnrollment || !notstartedEnrollment) {
        if (Boolean(profilePicture) || Boolean(profileAvatar)) {
          navigate(
            `${SOURCE_ROUTES.enrollment.path}/${getPageRouter(lastViewedPage)}`
          );
        } else {
          navigate(
            `${SOURCE_ROUTES.enrollment.path}/${ROUTES.enrollment.profileEdit.path}`
          );
        }
      }
    } else if (oldEnrollmentUrl) {
      window.location.href = oldEnrollmentUrl;
    }
  };

  const renderHeader = (context: string) => {
    return (
      <div>
        <div className="d-flex align-items-end align-items-md-center flex-row">
          {(isCompleteNotDual || isCompleteAndDual) && (
            <CheckCircleStyled size={40} className="mr-2 " />
          )}
          <h2 className="enrollment-context">
            {(isCompleteNotDual || isCompleteAndDual) ? t('heresToHealth') : headerText.get(context)}
          </h2>
        </div>
        <div className="body1 semiBold">{titleText.get(context)}</div>
        <div className="body1">
          {t('planYear')}
          {planYearStart?.toString()}-{enrollmentPlanYearEndsOn?.toString()}
        </div>
      </div>
    );
  };

  const renderBody = (context: string) => {
    const body = bodyText.get(context);
    const innerText = body && body[statusIndex];

    return (
      <div className="pt-3">
        <div className="body1">
          {innerText} {isNHandDual ? t('newHireEnrollmentBodyDual') : ''}
        </div>
      </div>
    );
  };

  const confirmationStatement = () => {
    const props = {
      enrollmentContext: enrollmentContext.enrollment_type_lookup_code,
      planYearStartsOn: enrollmentContext.plan_year_starts_on,
      planYearEndsOn: enrollmentContext.plan_year_ends_on,
    };
    return (
      <div className="pt-3">
        <div className="body1 semiBold">{t('receiveConfirmation')}</div>
        <div>
          <EmailConfirmationModal {...props} />
          {/* TODO_GL: this download does not work locally and may not work yet */}
          {/* it relies on a minified version of spyder to create the PDF for download */}
          <DownloadConfirmation {...props} />
        </div>
      </div>
    );
  };

  const getPendingText = (context: string | undefined) => {
    switch (context) {
      case NEW_HIRE_ENROLLMENT_TYPE:
        return t('newHire');
      case OPEN_ENROLLMENT_TYPE:
        return t('openEnrollmentHeader');
      case LIFE_EVENT_ENROLLMENT_TYPE:
        return t('lifeEventHeading');
      default:
        return '';
    }
  };

  const renderButton = () => {
    const firstContext = allContexts && allContexts[0];
    if (
      cardIndex > 0 &&
      INCOMPLETE_ENROLLMENT_STATUSES.includes(String(firstContext?.status))
    ) {
      return (
        <div className="">
          <PSBannerWrapper
            message={`${t('pending')} ${getPendingText(firstContext?.enrollment_type_lookup_code)}`}
            level="info"
            icon={true}
          />
        </div>
      );
    } else {
      return status === 'complete' ? (
        <PsButtonTuner
          buttonId={`update-my-benefits_${cardIndex}`}
          label={t('updateBenefits')}
          level="secondary"
          className="w-50"
          onClick={navToEnrollment}
        />
      ) : (
        <PsButtonTuner
          buttonId={`get_started_${cardIndex}`}
          label={
            status === 'started' ? t('continueEnrollmentBtn') : t('getStarted')
          }
          level="primary"
          className="w-50"
          onClick={navToEnrollment}
        />
      );
    }
  };

  const renderFooter = () => {
    const daysLeftOfEnrollment = calcDaysLeft(
      new Date(enrollmentContext.enrollment_ends_on || '')
    );
    return (
      <div className="context-footer d-md-flex w-100 justify-content-between">
        {renderButton()}
        <div className="justify-content-md-end pos-sm-abs days-to-go">
          <AlertBox
            innerText={`${daysLeftOfEnrollment} ${daysLeftOfEnrollment === 1 ? t('dayToGo') : t('daysToGo')}`}
          />
        </div>
      </div>
    );
  };

  return (
    <Card
      additionalClasses="context"
      dataTestId="enrollment-context-card"
      showBorder
    >
      <div className="border-bottom pb-3 w-100">{renderHeader(context)}</div>
      <div>{renderBody(context)}</div>
      {status === 'complete' && confirmationStatement()}
      <div>{renderFooter()}</div>
    </Card>
  );
};
